import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/i18n/hook';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const currentLang = i18n.language;
  const langLabel = currentLang === 'pt-BR' ? 'PT' : currentLang === 'de' ? 'DE' : 'EN';

  const getCategoryName = (c: typeof categories[0]) => {
    if (currentLang === 'de') return c.nameDe;
    if (currentLang === 'en') return c.nameEn;
    return c.name;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b-2 border-background-200">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="font-heading text-xl font-bold text-foreground-950 tracking-tight whitespace-nowrap">
          CSSBuy Spreadsheet
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-md transition-colors whitespace-nowrap">
            {t('nav.home')}
          </Link>
          <Link to="/article/cssbuy-spreadsheet-guide" className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-md transition-colors whitespace-nowrap">
            {t('nav.shoppingGuide')}
          </Link>
          <div className="relative" ref={catRef}>
            <button
              onClick={() => setCatOpen(!catOpen)}
              className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-md transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              {t('nav.categories')}
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
            </button>
            {catOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden py-1 max-h-[400px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    onClick={() => setCatOpen(false)}
                  >
                    {getCategoryName(cat)}
                  </a>
                ))}
              </div>
            )}
          </div>
          <Link to="/article/cssbuy-spreadsheet-guide" className="px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-700 rounded-md transition-colors whitespace-nowrap">
            {t('nav.article')}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center px-3 py-1.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t('cta.main1')}
          </a>
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-sm font-bold text-foreground-800 hover:bg-background-100 transition-colors"
            >
              {langLabel}
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-1 w-28 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden py-1">
                <button onClick={() => changeLang('pt-BR')} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                  Português
                </button>
                <button onClick={() => changeLang('en')} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                  English
                </button>
                <button onClick={() => changeLang('de')} className="block w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                  Deutsch
                </button>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 transition-colors"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/article/cssbuy-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              {t('nav.shoppingGuide')}
            </Link>
            <Link
              to="/article/cssbuy-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-md transition-colors"
            >
              {t('nav.article')}
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('nav.categories')}
            </p>
            <div className="grid grid-cols-2 gap-1 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors"
                >
                  {getCategoryName(cat)}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}